/**
 * /best-knowledge-base-software — SEO landing page for the knowledge_base
 * cluster: "knowledge base software" (≈9k), "free/best/internal/enterprise
 * knowledge base software", "knowledge base tools/solutions/platforms",
 * "knowledge base management system" (≈11k aggregate, KD ~16).
 *
 * Editorial angle: an HONEST, categorized list — same format as
 * BestProductManagementSoftware.tsx. We genuinely name the strongest KB
 * tools per job (team wiki, customer/help-desk, dev docs, open-source) and
 * place Draftlize in the ONE narrow row it owns: holding the decisions a
 * KB records, so they don't sit there confidently wrong. We do NOT pretend
 * Draftlize is a general knowledge base — honesty is the credibility.
 *
 * English-only + inline. Chinese variant in BestKnowledgeBaseSoftwareZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function BestKnowledgeBaseSoftware() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/best-knowledge-base-software"
      documentTitle="Best Knowledge Base Software (2026) · Draftlize"
      documentDescription="An honest, categorized list of the best knowledge base software in 2026 — team wikis, help-desk/support KBs, dev docs, open-source — and where each one actually wins."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 860 }}>
          <span className="label label-amber">Guide · Knowledge base software</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 60px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            The best knowledge base software,<br/>
            <em>sorted by what you&apos;re actually documenting.</em>
          </h1>
          <p className="lede">
            &quot;Best knowledge base software&quot; means four different products depending on who&apos;s asking — a team wiki, a customer help center, a developer docs site, or a self-hosted vault. This list names the strongest tool for each, honestly. We make one of these tools, and we&apos;ll be plain about it: for a wiki, a help center, or API docs, there are better-known, better-fit options than us, and we&apos;ll point you to them. Draftlize owns exactly one narrow row here — keeping the <em>decisions</em> a knowledge base records from quietly going out of date. Here&apos;s the honest map.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Pick by what the KB is <em>for.</em></h2>
          </div>
          <p>The mistake is shopping for one &quot;knowledge base&quot; to serve your team wiki, your customers, and your engineers at once. They pull in different directions. Here&apos;s who wins each job — and the one row nobody else really covers.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>What you&apos;re documenting</th>
              <th>Strong picks</th>
              <th className="ours">Where Draftlize fits</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Internal team wiki</td><td className="yes">Notion, Confluence, Slite, Slab</td><td className="ours no">Not a wiki</td></tr>
            <tr><td>Customer / help-desk KB</td><td className="yes">Zendesk Guide, Document360, Helpjuice</td><td className="ours no">Not customer-facing</td></tr>
            <tr><td>Developer / API docs</td><td className="yes">GitBook, ReadMe, Docusaurus</td><td className="ours partial">Holds the decisions docs cite</td></tr>
            <tr><td>Open-source / self-hosted</td><td className="yes">BookStack, Outline, Wiki.js</td><td className="ours no">Hosted, not self-host</td></tr>
            <tr><td>Knowing when an article went stale</td><td className="partial">— nobody really owns this —</td><td className="ours yes">This is the whole product</td></tr>
            <tr><td>A decision changes — flag dependent docs</td><td className="no">None of the above</td><td className="ours yes">Dependency graph + auto-stale marks</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">By category, <em>honestly.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Internal team wiki</h3>
            <p>
              <strong>Notion</strong> — the default for most teams; flexible pages, databases, everyone already has it.<br/>
              <strong>Confluence</strong> — the enterprise workhorse, deep Jira integration, heavier.<br/>
              <strong>Slite</strong> / <strong>Slab</strong> — cleaner, search-first, opinionated for company knowledge.<br/>
              <em>Honest take:</em> these are where team knowledge should live. Draftlize is not a wiki and won&apos;t pretend to be one.
            </p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Customer / help-desk KB</h3>
            <p>
              <strong>Zendesk Guide</strong> — the standard if support already runs on Zendesk.<br/>
              <strong>Document360</strong> — purpose-built public/private help centers with versioning.<br/>
              <strong>Helpjuice</strong> / <strong>Guru</strong> — strong search and in-workflow answers.<br/>
              <em>Honest take:</em> built for deflecting tickets and surfacing answers to customers. Not Draftlize&apos;s job.
            </p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Developer / API docs</h3>
            <p>
              <strong>GitBook</strong> — polished docs with Git sync; great for product + API.<br/>
              <strong>ReadMe</strong> — interactive API reference with try-it consoles.<br/>
              <strong>Docusaurus</strong> — open-source, docs-as-code, owns the static-site route.<br/>
              <em>Honest take:</em> these render the docs. What they don&apos;t do is know when the decision a doc describes has since changed.
            </p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">Open-source / self-hosted</h3>
            <p>
              <strong>BookStack</strong> — simple, tidy, self-hosted wiki; easy to run.<br/>
              <strong>Outline</strong> — modern, fast, great search; team knowledge on your own infra.<br/>
              <strong>Wiki.js</strong> — flexible, Markdown/WYSIWYG, many auth backends.<br/>
              <em>Honest take:</em> the &quot;free knowledge base software&quot; answer when data residency or cost rules out SaaS. Draftlize is hosted.
            </p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The one row that&apos;s <em>actually ours.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            A knowledge base stores what you knew. The thing none of them do is tell you when a recorded decision stopped being true — so a KB slowly fills with articles that are confidently, invisibly wrong. That narrow job is the whole of Draftlize.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Decisions become typed cards</h3>
            <p>A KB article buries the decision in prose — &quot;we chose Postgres because…&quot;. Draftlize captures that decision as a card: the choice, the rationale, the alternatives, the dependencies. Addressable by URL and ID, so a doc or spec can cite the exact decision instead of restating it and drifting from it.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one, dependents flag stale</h3>
            <p>This is what a wiki can&apos;t do. Each card knows what depends on it. Revisit the Postgres decision and every doc, spec, and runbook built on it turns stale automatically — the way a build system invalidates everything downstream of a changed file. The KB stops being a graveyard of half-true pages.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your AI agent reads and writes it</h3>
            <p>Keep your wiki for prose and your help center for customers. Draftlize is the decision layer underneath that Claude Code or Cursor reads and writes over MCP — so the agent answering &quot;why is it built this way&quot; reads the current decision, not a wiki page nobody updated since launch.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A knowledge base remembers what you wrote down. It can&apos;t tell you which of it is still true.
        </blockquote>
        <cite>Keep your wiki, your help center, your docs. Add the layer that flags what went stale.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the difference between a knowledge base and a wiki?', a: 'A wiki is a loosely structured, collaboratively edited set of pages — anyone adds and links freely. A knowledge base is usually more curated and search-optimized, often aimed at a specific audience (a support KB for customers, an internal KB for staff). In practice the terms overlap; the audience and the curation level are what actually differ.' },
            { q: 'What is the best free or open-source knowledge base software?', a: 'For self-hosted and free, BookStack (simplest to run), Outline (modern and fast), and Wiki.js (most flexible) are the strongest. They are the right call when data residency, cost, or customization rules out SaaS. If you just want zero-setup, Notion\'s free tier covers small teams.' },
            { q: 'Internal vs customer-facing knowledge base — same tool?', a: 'Usually not. Internal KBs (Notion, Confluence, Slite) optimize for fast team editing and breadth. Customer KBs (Zendesk Guide, Document360, Helpjuice) optimize for public publishing, SEO, versioning, and ticket deflection. Forcing one tool to do both tends to do neither well.' },
            { q: 'Does a knowledge base replace a PRD or a decision log?', a: 'No — and this is where teams get burned. A KB records knowledge as prose; it has no idea when a decision it documented changed, so dependent articles silently rot. A decision log or PRD with real dependency tracking is a different job. That gap is exactly what Draftlize fills underneath whatever KB you use.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Add the decision layer <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep the knowledge base you already use for wikis, help centers, and docs. Add Draftlize for the one thing none of them do: making sure the decisions they record stay current — and your AI agent reads the live version.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
